import { router } from 'expo-router';
import { memo, useMemo } from 'react';
import { Pressable, View } from 'react-native';

import { XStack, tokens } from '@fhs/ui';
import { selectors, useAppSelector } from '@fhs-legacy/frontend/src/state/global-state';
import { useOffersContext } from '@fhs-legacy/frontend/src/state/offers';
import { useOrderContext } from '@fhs-legacy/frontend/src/state/order';
import { EDIT_BUTTON } from '@fhs-legacy/frontend/src/utils/test-ids';
import { ICartEntry } from '@rbi-ctg/menu';

import EditQuantityToggle from './edit-quantity-toggle';
import ItemProfile from './item-profile';
import ItemPrice from './item-profile/item-price';
import RewardPointsBar from './reward-points-bar';

type Props = {
  item: ICartEntry;
};

function CartItem({ item }: Props) {
  const selectedOfferSelections = useAppSelector(selectors.loyalty.selectSelectedOfferSelections);
  const isRewardApplied = useMemo(
    () => selectedOfferSelections.find(selection => selection.lineId === item.cartId),
    [selectedOfferSelections, item.cartId]
  );

  const { isSelectedOfferCartEntry } = useOffersContext();
  const { cartEntries, setCartIdEditing } = useOrderContext();

  const handleItemEdit = () => {
    /* Updates the cartIdEditing to the new entry only if it exists
       cartIdEditing is used in fresh/ -> useEditFromCartState */
    if (
      isSelectedOfferCartEntry(item) ||
      cartEntries.find(({ cartId }) => cartId === item.cartId)
    ) {
      setCartIdEditing(item.cartId);
    }

    // Use push to ensure the back button goes to cart
    router.push({
      pathname: item.url,
      params: {
        isEdit: 'true',
        isFromCart: 'true',
      },
    });
  };

  const accessibilityLabel = `Edit ${item.name}, quantity ${item.quantity}, ${
    isRewardApplied ? 'reward applied' : 'no reward applied'
  }`;

  return (
    <View
      style={{
        padding: 16,
        borderWidth: 1,
        borderRadius: 8,
        borderColor: tokens.colors.$blackOpacity04,
      }}
      accessibilityRole="none"
      accessibilityLabel={`Cart item: ${item.name}`}
    >
      <XStack style={{ gap: 16 }}>
        <Pressable
          testID={EDIT_BUTTON}
          onPress={handleItemEdit}
          accessibilityRole="button"
          accessibilityLabel={accessibilityLabel}
          style={{ flex: 1 }}
        >
          <ItemProfile item={item} onEdit={handleItemEdit} />
        </Pressable>
        <View style={{ gap: 12 }}>
          <ItemPrice item={item} />
          <EditQuantityToggle item={item} />
        </View>
      </XStack>
      <RewardPointsBar item={item} />
    </View>
  );
}

export default memo(CartItem);
